// Components/WebinarForm.jsx
import React, { useRef, useState, useEffect } from "react";
import {
  X,
  Loader2,
  ImageIcon,
  CalendarDays,
  User,
  Type,
  CheckCircle2,
} from "lucide-react";
import {
  checkSlugAvailabilityAPI,
  createWebinarAPI,
  updateWebinarAPI,
} from "../services/apicollection";

const CATEGORY_OPTIONS = [
  "Software Development",
  "Communication Skill",
  "Data Science",
  "DevOps",
  "Digital Marketing"
];

const WebinarForm = ({ webinar, onSuccess, onCancel }) => {
  const isEditMode = !!webinar;
  
  const [formData, setFormData] = useState({
    topic: "",
    category: "",
    startDate: "",
    endDate: "",
    objective: "",
    audience: "",
    instructorName: "",
    instructorTitle: "",
    slug: "",
  });
  const [posterPreview, setPosterPreview] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifyingSlug, setVerifyingSlug] = useState(false);
  const [slugError, setSlugError] = useState(null);
  const fileInputRef = useRef(null);

  // Populate form data when editing
  useEffect(() => {
    if (webinar) {
      setFormData({
        topic: webinar.topic || "",
        category: webinar.category || "",
        startDate: webinar.startDate ? new Date(webinar.startDate).toISOString().slice(0, 16) : "",
        endDate: webinar.endDate ? new Date(webinar.endDate).toISOString().slice(0, 16) : "",
        objective: webinar.objective || "",
        audience: webinar.audience || "",
        instructorName: webinar.instructorName || "",
        instructorTitle: webinar.instructorTitle || "",
        slug: webinar.slug || "",
      });
      if (webinar.poster) {
        setPosterPreview(webinar.poster);
      }
      setVerifyingSlug(true);
    }
  }, [webinar]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePosterChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please upload a valid image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPosterPreview(event.target?.result || null);
      setPosterFile(file);
    };
    reader.readAsDataURL(file);
  };

  const verifySlug = async (slug) => {
    if (!slug) {
      alert("Please enter a slug to check");
      return;
    }
    try {
      const res = await checkSlugAvailabilityAPI(slug);
      if (res?.status) {
        alert("Slug is available");
        setSlugError(null);
        setVerifyingSlug(true);
      } else {
        alert("Slug is already taken, please choose another one");
        setSlugError("Slug is already taken, please choose another one");
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Failed to verify slug";
      alert(msg);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isEditMode) {
      if (!verifyingSlug && formData.slug.length > 5) {
        alert("Please verify the slug before submitting");
        return;
      } else if (slugError) {
        alert(slugError);
        return;
      }
    }

    if (!formData.topic || !formData.startDate || !formData.endDate) {
      alert("Topic, start date and end date are required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        posterFile,
      };
      
      let res;
      if (isEditMode) {
        res = await updateWebinarAPI(webinar._id, payload);
      } else {
        res = await createWebinarAPI(payload);
      }
      
      if (res?.status === false) {
        alert(res?.message || `Failed to ${isEditMode ? 'update' : 'create'} webinar`);
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || `Failed to ${isEditMode ? 'update' : 'create'} webinar`;
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-200">
            {isEditMode ? "Edit Event" : "Create Event"}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {isEditMode ? "Update event details and poster." : "Configure upcoming event details and poster."}
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#13233a] text-slate-500 hover:bg-slate-200"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Topic <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
              <Type size={16} className="text-slate-400" />
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => handleChange("topic", e.target.value)}
                className="h-7 w-full border-none bg-transparent text-sm outline-none"
                placeholder="e.g. DevOps for Beginners"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange("category", e.target.value)}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            >
              <option value="">Select category</option>
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Start date & time <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
              <CalendarDays size={16} className="text-slate-400" />
              <input
                type="datetime-local"
                value={formData.startDate}
                onChange={(e) => handleChange("startDate", e.target.value)}
                className="h-7 w-full border-none bg-transparent text-sm outline-none"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              End date & time <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
              <CalendarDays size={16} className="text-slate-400" />
              <input
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) => handleChange("endDate", e.target.value)}
                className="h-7 w-full border-none bg-transparent text-sm outline-none"
                required
              />
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Objective
            </label>
            <textarea
              value={formData.objective}
              onChange={(e) => handleChange("objective", e.target.value)}
              rows={3}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 resize-none"
              placeholder="Short summary of what attendees will learn"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Target audience
            </label>
            <textarea
              value={formData.audience}
              onChange={(e) => handleChange("audience", e.target.value)}
              rows={3}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 resize-none"
              placeholder="Who is this webinar for?"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Instructor name
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
              <User size={16} className="text-slate-400" />
              <input
                type="text"
                value={formData.instructorName}
                onChange={(e) => handleChange("instructorName", e.target.value)}
                className="h-7 w-full border-none bg-transparent text-sm outline-none"
                placeholder="e.g. Rahul Sharma"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Instructor title
            </label>
            <input
              type="text"
              value={formData.instructorTitle}
              onChange={(e) => handleChange("instructorTitle", e.target.value)}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              placeholder="e.g. Senior Software Engineer"
            />
          </div>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-1">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Slug (URL identifier) <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
              <Type size={16} className="text-slate-400" />
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => {
                  handleChange("slug", e.target.value);
                  if (!isEditMode) {
                    setVerifyingSlug(false);
                    setSlugError(null);
                  }
                }}
                className="h-7 w-full border-none bg-transparent text-sm outline-none"
                placeholder="e.g. devops-for-beginners-workshop"
                required
                disabled={isEditMode}
              />
              {!isEditMode && formData.slug.length > 5 && verifyingSlug ? (
                <div className="text-green-600 flex items-center gap-1.5 whitespace-nowrap">
                  <CheckCircle2 size={14} /> Available
                </div>
              ) : (
                !isEditMode && formData.slug.length > 5 && (
                  <button
                    type="button"
                    onClick={() => verifySlug(formData.slug)}
                    className="text-xs whitespace-nowrap font-medium text-slate-200 underline hover:no-underline"
                  >
                    Check availability
                  </button>
                )
              )}
            </div>
            {slugError && (
              <div className="text-red-600 text-xs mt-1">
                * {slugError}
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-400">
              <ImageIcon size={16} className="text-slate-400" />
              Event poster
            </label>
            <div className="mt-1 flex flex-col gap-3 rounded-lg border border-dashed border-slate-300 p-4 text-center">
              {posterPreview ? (
                <div className="flex flex-col items-center gap-3">
                  <img
                    src={posterPreview}
                    alt="Preview"
                    className="h-32 w-full max-w-xs rounded-md object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPosterPreview(null);
                      setPosterFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="text-xs font-medium text-red-600 hover:text-red-700"
                  >
                    Remove image
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-400">
                    Drag & drop poster here or{" "}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="font-semibold text-slate-200 underline hover:no-underline"
                    >
                      browse files
                    </button>
                  </p>
                  <p className="text-[11px] text-slate-400">
                    JPG / PNG up to 5MB recommended size 16:9
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePosterChange}
                className="hidden"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[#1d3350] px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-[#0d1b2e]"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-700 disabled:opacity-60"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {isEditMode ? "Update Event" : "Create Event"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WebinarForm;